import React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { payerOptions, sourceOptions } from "../careGapConstants";
import PayerChip from "./payerChip";

const BulkActionsBar = ({
  openGaps,
  selectedIds,
  bulkSourceType,
  setBulkSourceType,
  bulkPayers,
  setBulkPayers,
  bulkEditMode,
  bulkSourceDirty,
  submitting,
  onToggleSelectAll,
  onBulkUpdateSource,
  onCloseSelected,
  onRejectSelected,
  onEditText,
  onSaveNotes,
  onCancelEdit,
}) => {
  const selectedCount = selectedIds.length;
  const allSelected = openGaps.length > 0 && selectedCount === openGaps.length;
  const indeterminate = selectedCount > 0 && selectedCount < openGaps.length;

  const handlePayerToggle = (payer) => {
    setBulkPayers((prev) =>
      prev.includes(payer) ? prev.filter((p) => p !== payer) : [...prev, payer],
    );
  };

  const isBulkSourceValid =
    bulkSourceType === "AI" ||
    (bulkSourceType === "PAYER" && bulkPayers.length);

  return (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        borderRadius: "18px",
        border: "1px solid #d7dde5",
        background: "#f8fafc",
      }}
    >
      <Stack spacing={1.2}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={indeterminate}
              onChange={onToggleSelectAll}
              sx={{ p: 0.5 }}
            />
          }
          label={
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                sx={{ fontWeight: 700, color: "#273142", fontSize: 13 }}
              >
                Select all
              </Typography>
              <Typography sx={{ color: "#667085", fontSize: 13 }}>
                {selectedCount} selected
              </Typography>
            </Stack>
          }
          sx={{ m: 0 }}
        />

        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
        >
          <Box sx={{ minWidth: 160, width: 160 }}>
            <TextField
              select
              fullWidth
              size="small"
              value={bulkSourceType}
              onChange={(e) => {
                setBulkSourceType(e.target.value);
                setBulkPayers([]);
              }}
              disabled={!selectedCount}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  background: "#fff",
                  fontSize: 13,
                  minHeight: 36,
                },
              }}
            >
              {sourceOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Button
            variant="contained"
            disabled={
              !selectedCount ||
              !bulkSourceDirty ||
              !isBulkSourceValid ||
              submitting
            }
            onClick={onBulkUpdateSource}
            sx={buttonPrimarySx}
          >
            Bulk update source
          </Button>

          <Button
            variant="outlined"
            disabled={!selectedCount || submitting}
            onClick={onCloseSelected}
            sx={buttonSecondarySx}
          >
            Close selected
          </Button>

          <Button
            variant="outlined"
            disabled={!selectedCount || submitting}
            onClick={onRejectSelected}
            sx={buttonSecondarySx}
          >
            Reject selected
          </Button>

          {!bulkEditMode ? (
            <Button
              variant="outlined"
              disabled={!selectedCount || submitting}
              onClick={onEditText}
              sx={buttonSecondarySx}
            >
              Bulk edit text
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                disabled={!selectedCount || submitting}
                onClick={onSaveNotes}
                sx={buttonPrimarySx}
              >
                Save all selected
              </Button>

              <Button
                variant="outlined"
                disabled={submitting}
                onClick={onCancelEdit}
                sx={buttonSecondarySx}
              >
                Cancel edit
              </Button>
            </>
          )}
        </Stack>

        {bulkSourceType === "PAYER" && selectedCount > 0 && (
          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
            {payerOptions.map((payer) => (
              <PayerChip
                key={payer}
                payer={payer}
                small
                selected={bulkPayers.includes(payer)}
                onClick={() => handlePayerToggle(payer)}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

const buttonPrimarySx = {
  height: 36,
  px: 1.5,
  borderRadius: "12px",
  textTransform: "none",
  fontWeight: 700,
  fontSize: 13,
  boxShadow: "none",
  background: "#db2777",
  "&:hover": {
    background: "#be185d",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    background: "#e5e7eb",
    color: "#98a2b3",
  },
};

const buttonSecondarySx = {
  height: 36,
  px: 1.5,
  borderRadius: "12px",
  textTransform: "none",
  fontWeight: 700,
  fontSize: 13,
  color: "#273142",
  borderColor: "#d0d5dd",
};

export default BulkActionsBar;
